import { schemas } from '#oaf_schema.registry';
import { securityReviewStore, selectTechnologyDetails } from '../stores/securityReviewStore';
import { merge } from './objectUtilities';
export const SCHEMA_ID_TO_STEP_ERROR_MAP = {
  'securityReview/plugins/ContactInformation': '1',
  'securityReview/plugins/TechnologyDetails': '2',
  'securityReview/plugins/Reports': '3',
  'securityReview/plugins/TestEnvironments': '4'
};
export function createContactInformationFields(securityReview) {
  const steps = [generateStepMetadata(() => {
    var _selectContactInfo;
    return (_selectContactInfo = selectContactInfo(securityReview)) === null || _selectContactInfo === void 0 ? void 0 : _selectContactInfo.name;
  }, () => 'name', getContactInformationSchemaId), generateStepMetadata(() => {
    var _selectContactInfo2;
    return (_selectContactInfo2 = selectContactInfo(securityReview)) === null || _selectContactInfo2 === void 0 ? void 0 : _selectContactInfo2.primaryEmail;
  }, () => 'primaryEmail', getContactInformationSchemaId), generateStepMetadata(() => {
    var _selectContactInfo3;
    return (_selectContactInfo3 = selectContactInfo(securityReview)) === null || _selectContactInfo3 === void 0 ? void 0 : _selectContactInfo3.secondaryEmail;
  }, () => 'secondaryEmail', getContactInformationSchemaId)];
  return steps;
}
export function createTechnicalDetailsFields() {
  const techDetails = selectTechnologyDetails(securityReviewStore.getState());
  return [{
    getName: () => 'detailedDescription',
    checkComplete: () => {
      return Boolean(techDetails.technicalDescription);
    }
  }, {
    getName: () => 'includesRequiresWebSvc',
    checkComplete: () => {
      return techDetails.includesRequiresWebSvc ? Boolean(techDetails.includesRequiresWebSvcDetails) : true;
    }
  }, {
    getName: () => 'useOtherPlatform',
    checkComplete: () => {
      return techDetails.useOtherPlatform ? Boolean(techDetails.useOtherPlatformDetails) : true;
    }
  }, {
    getName: () => 'integratesWithExternalTech',
    checkComplete: () => {
      return techDetails.integratesWithExternalTech ? Boolean(techDetails.integratesWithExternalTechDetails) : true;
    }
  }, {
    getName: () => 'containsSFTech',
    checkComplete: () => {
      return techDetails.containsSFTech ? Boolean(techDetails.containsSFTechDetails) : true;
    }
  }, {
    getName: () => 'storesAuthToken',
    checkComplete: () => {
      return techDetails.storesAuthToken ? Boolean(techDetails.storesAuthTokenDetails) : true;
    }
  }, {
    getName: () => 'storesOutsideSF',
    checkComplete: () => {
      return techDetails.storesOutsideSF ? Boolean(techDetails.storesOutsideSFDetails) : true;
    }
  }, {
    getName: () => 'devicesTarget',
    checkComplete: () => {
      return techDetails.isMobileAppAvailable && techDetails.devicesTarget ? Boolean(techDetails.devicesTargetDetails) : true;
    }
  }, {
    getName: () => 'includesBrowserExtension',
    checkComplete: () => {
      return techDetails.isMobileAppAvailable && techDetails.includesBrowserExtension ? Boolean(techDetails.browserExtensionDetails) : true;
    }
  }, {
    getName: () => 'includesClientApp',
    checkComplete: () => {
      return techDetails.isMobileAppAvailable && techDetails.includesClientApp ? Boolean(techDetails.clientAppDetails) : true;
    }
  }, {
    getName: () => 'mobileAppAvailable',
    checkComplete: () => {
      return techDetails.isMobileAppAvailable ? techDetails.devicesTarget || techDetails.includesBrowserExtension || techDetails.includesClientApp : true;
    }
  }, {
    getName: () => 'atLeastOneFieldIsOn',
    checkComplete: () => {
      return techDetails.includesRequiresWebSvc || techDetails.useOtherPlatform || techDetails.integratesWithExternalTech || techDetails.containsSFTech || techDetails.storesAuthToken || techDetails.storesOutsideSF || techDetails.isApiOnly || techDetails.isMobileAppAvailable && (techDetails.devicesTarget || techDetails.includesBrowserExtension || techDetails.includesClientApp);
    }
  }];
}
/*
 * createDocumentsFields
 * @securityReview
 * @state is optional and added only for testing purposes. This allows to pass a mocked store as a paramter.
 * usually we should mock the import for all files but that's not true for some test at the moment.
 */
export function createDocumentsFields(securityReview, state) {
  const techDetails = selectTechnologyDetails(state !== null && state !== void 0 ? state : securityReviewStore.getState());
  return [{
    getName: () => 'sampleAPICallouts',
    checkComplete: () => {
      return techDetails && validateIfSampleAPICalloutsIsCompleted(securityReview, techDetails);
    }
  }, {
    getName: () => 'securityScanner',
    checkComplete: () => {
      return techDetails && validateIfSecurityScannerIsCompleted(securityReview, techDetails);
    }
  }, {
    getName: () => 'falsePositives',
    checkComplete: () => {
      return validateIfFalsePositivesDocsIsCompleted(securityReview);
    }
  }, {
    getName: () => 'sfCodeAnalyzer',
    checkComplete: () => {
      return validateIfSFCodeAnalyzerDocsIsCompleted(securityReview, techDetails);
    }
  }, {
    getName: () => 'architectureAndUsage',
    checkComplete: () => {
      return validateArchitectureUsageReportCompleated(securityReview);
    }
  }];
}

/**
 * Sample API Callouts: Will be required upload if includesRequiresWebSvc or
 * integratesWithExternalTech are toggled on in Step 3.
 * */
function validateIfSampleAPICalloutsIsCompleted(securityReview, techDetails) {
  if (techDetails && (techDetails.includesRequiresWebSvc || techDetails.integratesWithExternalTech || techDetails.isApiOnly)) {
    var _selectReports;
    const sampleApiCallouts = (_selectReports = selectReports(securityReview)) === null || _selectReports === void 0 ? void 0 : _selectReports.sampleAPICallouts;
    return sampleApiCallouts && sampleApiCallouts.length > 0 && sampleApiCallouts[0].mediaId !== '' && sampleApiCallouts[0].fileTitle !== '' || false;
  }
  return true;
}
/**
 * Security Scanner Reports: Is required if containsSFTech or includesRequiresWebSvc
 * or integratesWithExternalTech or isMobileAppAvailable is toggled on in Step 3
 */
function validateIfSecurityScannerIsCompleted(securityReview, techDetails) {
  if (techDetails && (techDetails.containsSFTech || techDetails.includesRequiresWebSvc || techDetails.integratesWithExternalTech || techDetails.isMobileAppAvailable || techDetails.isApiOnly)) {
    var _selectReports2;
    const securityScanner = (_selectReports2 = selectReports(securityReview)) === null || _selectReports2 === void 0 ? void 0 : _selectReports2.securityScanner;
    return securityScanner && securityScanner.length > 0 && securityScanner[0].mediaId !== '' && securityScanner[0].fileTitle !== '' || false;
  }
  return true;
}
function validateArchitectureUsageReportCompleated(securityReview) {
  var _selectReports3;
  const architecutreAndUsage = (_selectReports3 = selectReports(securityReview)) === null || _selectReports3 === void 0 ? void 0 : _selectReports3.architectureAndUsage;
  if (architecutreAndUsage && architecutreAndUsage.length > 0 && architecutreAndUsage[0].mediaId !== '' && architecutreAndUsage[0].fileTitle !== '') {
    return true;
  }
  return false;
}

/**
 * False Positive Documentation: Should require at least 1 upload if any reports are uploaded
 * in Security Scanner Reports.
 */
function validateIfFalsePositivesDocsIsCompleted(securityReview) {
  var _selectReports4, _selectReports5;
  if ((_selectReports4 = selectReports(securityReview)) !== null && _selectReports4 !== void 0 && _selectReports4.noFalsePositivesConfirmation) {
    return true;
  }
  const securityScanner = (_selectReports5 = selectReports(securityReview)) === null || _selectReports5 === void 0 ? void 0 : _selectReports5.securityScanner;
  if (securityScanner && securityScanner.length > 0 && securityScanner[0].mediaId !== '') {
    var _selectReports6;
    const falsePositives = (_selectReports6 = selectReports(securityReview)) === null || _selectReports6 === void 0 ? void 0 : _selectReports6.falsePositives;
    return falsePositives && falsePositives.length > 0 && falsePositives[0].mediaId !== '' && falsePositives[0].fileTitle !== '' || false;
  }
  return true;
}

/**
 * SF Code Analyzer Documentation: Should require at least 1 upload if any reports are uploaded
 * in Security Scanner Reports. If not files uploaded, then we need the checkbox checked and
 * a brief description.
 */
function validateIfSFCodeAnalyzerDocsIsCompleted(securityReview, techDetails) {
  if (techDetails && techDetails.containsSFTech) {
    const reports = selectReports(securityReview);
    if (reports !== null && reports !== void 0 && reports.sfCodeAnalyzer && (reports === null || reports === void 0 ? void 0 : reports.sfCodeAnalyzer.length) > 0 && (reports === null || reports === void 0 ? void 0 : reports.sfCodeAnalyzer[0].mediaId) !== '' && (reports === null || reports === void 0 ? void 0 : reports.sfCodeAnalyzer[0].fileTitle) !== '') {
      return true;
    } else if (reports !== null && reports !== void 0 && reports.noSFCodeAnalyzerConfirmation && reports !== null && reports !== void 0 && reports.noSFCodeAnalyzerReasons && (reports === null || reports === void 0 ? void 0 : reports.noSFCodeAnalyzerReasons) !== '') {
      return true;
    }
    return false;
  }
  return true;
}
export function createEnvironmentsFields(securityReview) {
  const techDetails = selectTechnologyDetails(securityReviewStore.getState());
  return [{
    getName: () => 'salesforceOrgs',
    checkComplete: () => {
      return techDetails && validateSalesforceOrgsIsCompleted(securityReview, techDetails);
    }
  }, {
    getName: () => 'authInfo',
    checkComplete: () => {
      return techDetails && validateWebAppItemsIsCompleted(securityReview, techDetails);
    }
  }, {
    getName: () => 'mobileApps',
    checkComplete: () => {
      return techDetails && validateMobileAppsIsCompleted(securityReview, techDetails);
    }
  }];
}

/**
 * If "..contains Salesforce Platform Technology.." is toggled on in Step 3 then
 * at least one input is required in "Username and Password Authentication"
 */
export function validateSalesforceOrgsIsCompleted(securityReview, techDetails) {
  if (techDetails && techDetails.containsSFTech) {
    var _selectTestEnviroment;
    const salesforceOrgs = (_selectTestEnviroment = selectTestEnviroments(securityReview)) === null || _selectTestEnviroment === void 0 ? void 0 : _selectTestEnviroment.salesforceOrgs;
    return salesforceOrgs && salesforceOrgs.length > 0 && (Boolean(salesforceOrgs[0].id) || Boolean(salesforceOrgs[0].username) && Boolean(salesforceOrgs[0].password)) || false;
  }
  return true;
}

/**
 * If "...requires a web application or web service" or "...integrates with technology external
 * to Salesforce platform" is toggled on in Step 3 then at least one input is required from
 * "Username and Password Authentication" or "API, SAML, oAuth"
 */
export function validateWebAppItemsIsCompleted(securityReview, techDetails) {
  if (techDetails && (techDetails.includesRequiresWebSvc || techDetails.integratesWithExternalTech || techDetails.isApiOnly)) {
    var _selectTestEnviroment2, _selectTestEnviroment3;
    const webAppItems = (_selectTestEnviroment2 = selectTestEnviroments(securityReview)) === null || _selectTestEnviroment2 === void 0 ? void 0 : _selectTestEnviroment2.webAppAndServices;
    const salesforceOrgs = (_selectTestEnviroment3 = selectTestEnviroments(securityReview)) === null || _selectTestEnviroment3 === void 0 ? void 0 : _selectTestEnviroment3.salesforceOrgs;
    return webAppItems && webAppItems.length > 0 && Boolean(webAppItems[0].loginUrl) || salesforceOrgs && salesforceOrgs.length > 0 && (Boolean(salesforceOrgs[0].id) || Boolean(salesforceOrgs[0].username) && Boolean(salesforceOrgs[0].password)) || false;
  }
  return true;
}

/**
 * If "...is your solution a mobile app?" is toggled on in Step 3 then
 * at least one input is required in "Mobile Apps" for Applications or Installation Links
 */

export function validateMobileAppsIsCompleted(securityReview, techDetails) {
  if (techDetails && techDetails.isMobileAppAvailable) {
    var _selectTestEnviroment4, _selectTestEnviroment5, _selectTestEnviroment6, _selectTestEnviroment7;
    const mobileApplications = (_selectTestEnviroment4 = selectTestEnviroments(securityReview)) === null || _selectTestEnviroment4 === void 0 ? void 0 : (_selectTestEnviroment5 = _selectTestEnviroment4.mobileApps) === null || _selectTestEnviroment5 === void 0 ? void 0 : _selectTestEnviroment5.applications;
    const installationLink = (_selectTestEnviroment6 = selectTestEnviroments(securityReview)) === null || _selectTestEnviroment6 === void 0 ? void 0 : (_selectTestEnviroment7 = _selectTestEnviroment6.mobileApps) === null || _selectTestEnviroment7 === void 0 ? void 0 : _selectTestEnviroment7.installationLinks;
    return mobileApplications && mobileApplications.length > 0 && Boolean(mobileApplications[0].mediaId) && Boolean(mobileApplications[0].platform) && Boolean(mobileApplications[0].description) || installationLink && installationLink.length > 0 && Boolean(installationLink[0].installationLink) && Boolean(installationLink[0].description) || false;
  }
  return true;
}
export function generateStepMetadata(valueSelector, nameSelector, schemaSelector, options = {
  minItems: 0
}) {
  return {
    getName: nameSelector,
    getValue: valueSelector,
    getSchema: () => {
      var _schemas$schemaSelect;
      const schema = (_schemas$schemaSelect = schemas[schemaSelector()]) === null || _schemas$schemaSelect === void 0 ? void 0 : _schemas$schemaSelect.get();
      return merge(schema === null || schema === void 0 ? void 0 : schema.properties[nameSelector()], options.minItems > 0 ? {
        minItems: options.minItems
      } : {});
    },
    isRequired: () => {
      var _schemas$schemaSelect2;
      const schema = (_schemas$schemaSelect2 = schemas[schemaSelector()]) === null || _schemas$schemaSelect2 === void 0 ? void 0 : _schemas$schemaSelect2.get();
      return schema && 'required' in schema && schema.required.includes(nameSelector());
    }
  };
}
export function getPlugin(state, pluginType) {
  var _state$plugins, _state$plugins$find;
  return (_state$plugins = state.plugins) === null || _state$plugins === void 0 ? void 0 : (_state$plugins$find = _state$plugins.find(test => test.pluginType === pluginType)) === null || _state$plugins$find === void 0 ? void 0 : _state$plugins$find.data;
}
export function selectContactInfo(sr) {
  return getPlugin(sr, 'securityReview/plugins/ContactInformation');
}
function getContactInformationSchemaId() {
  return 'securityReview/plugins/ContactInformation';
}
export function selectReports(sr) {
  return getPlugin(sr, 'securityReview/plugins/Reports');
}
export function selectTestEnviroments(sr) {
  return getPlugin(sr, 'securityReview/plugins/TestEnvironments');
}